import {
  useRef,
  useState,
  useCallback,
  useEffect,
  ChangeEvent,
  DragEvent,
  KeyboardEvent,
  MouseEvent,
} from 'react';
import { useTheme } from 'styled-components';
import Icon from '../../../../components/common/Icon/Icon';
import { IconKeys } from '../../../../components/common/Icon/IconKeys';
import { Button } from '../../../../components/ui/Button/Button';
import { useAppTheme } from '../../../../theme/ThemeContext';
import { ThemeType } from '../../../../theme/themeType';
import S from './UploadAudioFileModalStyles';
import {
  ACCEPT_AUDIO,
  formatFileSize,
  formatDuration,
  getAudioDuration,
  isWithinSizeLimit,
} from './uploadAudioUtils';
import uploadAudio from '../images/upload-icon.svg';
import uploadAudioLight from '../images/upload-icon-light.svg';
import tickIcon from '../images/tick-icon.svg';
import audioTumbnail from '../images/audio-thumbnail.png';

export type UploadAudioFileItem = {
  id: string;
  file: File;
  name: string;
  durationSec: number | null;
  sizeFormatted: string;
  error: string | null;
};

export type ValidatedAudioItem = {
  file: File;
  name: string;
  durationSec: number | null;
  sizeFormatted: string;
};

const MAX_FILES_PER_FLEET = 5;
const MIN_NAME_LENGTH = 3;
const MAX_NAME_LENGTH = 30;

function hasAllowedFormat(name: string): boolean {
  const lower = name.toLowerCase();
  return lower.endsWith('.aac') || lower.endsWith('.mp3');
}

function getBaseNameBeforeExtension(name: string): string {
  const lower = name.toLowerCase();
  if (lower.endsWith('.aac')) return name.slice(0, -4);
  if (lower.endsWith('.mp3')) return name.slice(0, -4);
  return name;
}

type Props = {
  onClose: () => void;
  onUploadFiles?: (items: ValidatedAudioItem[]) => void;
  existingCount?: number;
  existingNames?: Set<string>;
  isUploading?: boolean;
  errorMessage?: string | null;
  failedFileNamesFromUpload?: string[] | null;
  onUploadResultApplied?: () => void;
};

function makeId(): string {
  return `file-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export default function UploadAudioFileModal({
  onClose,
  onUploadFiles,
  existingCount = 0,
  existingNames = new Set(),
  isUploading = false,
  errorMessage = null,
  failedFileNamesFromUpload = null,
  onUploadResultApplied,
}: Props): JSX.Element {
  const theme = useTheme() as ThemeType;
  const { themeMode } = useAppTheme();
  const uploadIcon = themeMode === 'light' ? uploadAudioLight : uploadAudio;
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileItems, setFileItems] = useState<UploadAudioFileItem[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const maxNew = Math.max(0, MAX_FILES_PER_FLEET - existingCount);

  const updateDuration = useCallback(async (file: File, id: string) => {
    try {
      const duration = await getAudioDuration(file);
      setFileItems((prev) => prev.map((item) => {
        if (item.id === id) {
          return { ...item, durationSec: duration };
        }
        return item;
      }));
    } catch {
      // ignore errors and keep duration as null
    }
  }, []);

  useEffect(() => {
    if (failedFileNamesFromUpload == null || failedFileNamesFromUpload.length === 0) return;
    const failedSet = new Set(
      failedFileNamesFromUpload.map((s) => {
        const withReason = s.trim();
        const paren = withReason.indexOf(' (');
        return paren >= 0 ? withReason.slice(0, paren).trim() : withReason;
      }),
    );
    setFileItems((prev) => prev.filter((item) => failedSet.has(item.name)));
    onUploadResultApplied?.();
  }, [failedFileNamesFromUpload, onUploadResultApplied]);

  const addFiles = useCallback((files: FileList | File[]): void => {
    const list = Array.from(files);
    if (list.length === 0) return;

    setFileItems((prev) => {
      const slotsLeft = maxNew - prev.length;
      if (slotsLeft <= 0) return prev;

      const take = Math.min(list.length, slotsLeft);
      const prevNames = new Set(prev.map((p) => p.name.toLowerCase()));

      const newItems = list.slice(0, take).map((file) => {
        const id = makeId();
        const { name } = file;
        let error: string | null = null;
        if (!hasAllowedFormat(name)) {
          error = 'Format must be .aac or .mp3';
        } else if (!isWithinSizeLimit(file)) {
          error = 'Max size: 5 MB';
        } else {
          const baseName = getBaseNameBeforeExtension(name);
          const baseNameLen = baseName.length;
          if (baseNameLen < MIN_NAME_LENGTH) {
            error = `Name must be at least ${MIN_NAME_LENGTH} characters`;
          } else if (baseNameLen > MAX_NAME_LENGTH) {
            error = `Name must be at most ${MAX_NAME_LENGTH} characters`;
          } else {
            const nameLower = name.toLowerCase();
            const isDuplicate = existingNames.has(nameLower) || prevNames.has(nameLower);
            prevNames.add(nameLower);
            if (isDuplicate) error = 'Filename already exists';
          }
        }

        const item = {
          id,
          file,
          name,
          durationSec: null,
          sizeFormatted: formatFileSize(file.size),
          error,
        };
        updateDuration(file, id);
        return item;
      });

      return [...prev, ...newItems].slice(0, maxNew);
    });
  }, [maxNew, existingNames, updateDuration]);

  function onInputChange(e: ChangeEvent<HTMLInputElement>): void {
    const input = e.target;
    const fileList = input.files ? Array.from(input.files) : [];
    input.value = '';
    if (!fileList.length) return;
    addFiles(fileList);
  }

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const files = e.dataTransfer?.files;
    if (files?.length) addFiles(files);
  }, [addFiles]);

  const removeFile = useCallback((id: string): void => {
    setFileItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const hasAnyError = fileItems.some((i) => i.error);
  const canUpload = fileItems.length > 0 && !hasAnyError && !isUploading;
  const canAdd = fileItems.length < maxNew;

  const handleDropZoneClick = useCallback((): void => {
    if (canAdd) inputRef.current?.click();
  }, [canAdd]);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    if (!canAdd) return;
    setIsDragOver(true);
  }, [canAdd]);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDropZoneKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>): void => {
    if ((e.key === 'Enter' || e.key === ' ') && canAdd) {
      e.preventDefault();
      inputRef.current?.click();
    }
  }, [canAdd]);

  const handleRemoveClick = useCallback((e: MouseEvent<HTMLButtonElement>): void => {
    const id = e.currentTarget.getAttribute('data-id');
    if (id) removeFile(id);
  }, [removeFile]);

  const handleUpload = useCallback((): void => {
    const valid = fileItems.filter((i) => !i.error);
    if (valid.length === 0) return;
    onUploadFiles?.(
      valid.map((i) => ({
        file: i.file,
        name: i.name,
        durationSec: i.durationSec,
        sizeFormatted: i.sizeFormatted,
      })),
    );
  }, [fileItems, onUploadFiles]);

  return (
    <S.Wrapper>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_AUDIO}
        multiple
        onChange={onInputChange}
        style={{ display: 'none' }}
        aria-hidden
      />

      <S.DropZone
        isDragOver={isDragOver}
        $atMax={!canAdd}
        onClick={handleDropZoneClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        role="button"
        tabIndex={canAdd ? 0 : -1}
        onKeyDown={handleDropZoneKeyDown}
        aria-label={canAdd ? 'Drop your audio file here or click to browse' : 'Maximum 5 files. Remove a file to add more.'}
      >
        <S.DropZoneIcon aria-hidden><img src={uploadIcon} alt="Upload Audio" /></S.DropZoneIcon>
        <S.DropZoneText>Drop your audio file here</S.DropZoneText>
        <S.DropZoneText>or click to browse from your computer</S.DropZoneText>
        {!canAdd && (
          <S.DropZoneHint>Only 5 files can be uploaded. Remove a file to add more.</S.DropZoneHint>
        )}
      </S.DropZone>

      {errorMessage && (
        <S.FileError style={{ marginBottom: 8, display: 'block' }}>{errorMessage}</S.FileError>
      )}

      <S.RulesList>
        <li><img className="check" src={tickIcon} alt="Tick Icon" />Format: .aac or .mp3</li>
        <li><img className="check" src={tickIcon} alt="Tick Icon" />Name: 3–30 characters</li>
        <li><img className="check" src={tickIcon} alt="Tick Icon" />Max size: 5 MB</li>
        <li><img className="check" src={tickIcon} alt="Tick Icon" />Limit: 5 files per fleet</li>
      </S.RulesList>

      {fileItems.length > 0 && (
        <S.FileListSection>
          <S.FileListScroll>
            {fileItems.map((item) => (
              <S.FileItem key={item.id} hasError={!!item.error}>
                <S.PlayIcon aria-hidden><img src={audioTumbnail} alt="Audio Icon" /></S.PlayIcon>
                <S.FileInfo>
                  <S.FileName title={item.name}>{item.name}</S.FileName>
                  <S.FileMeta>
                    {item.durationSec != null ? formatDuration(item.durationSec) : '—'}
                    {' · '}{item.sizeFormatted}
                  </S.FileMeta>
                  {item.error && <S.FileError>{item.error}</S.FileError>}
                </S.FileInfo>
                <S.RemoveButton
                  type="button"
                  data-id={item.id}
                  onClick={handleRemoveClick}
                  aria-label={`Remove ${item.name}`}
                >
                  <Icon iconKey={IconKeys.CLOSE} />
                </S.RemoveButton>
              </S.FileItem>
            ))}
          </S.FileListScroll>
        </S.FileListSection>
      )}

      <S.Actions>
        <Button padding="7px 24px" onClick={onClose} disabled={isUploading}>Cancel</Button>
        <Button
          padding="7px 24px"
          disabled={!canUpload}
          onClick={handleUpload}
          background={theme.colors.main_cta}
          textColor={theme.colors.black}
        >
          {isUploading ? 'Uploading…' : 'Upload Files'}
        </Button>
      </S.Actions>
    </S.Wrapper>
  );
}
